import { DataNode } from '../../../types'
import { PromoteDemoteActions } from '../constants'
import { PromoteDemoteAction } from '../types'

// Cached lookups for O(1) performance after initial tree traversal
let depthLookup = new Map<string, number>()
let hierarchyDepthLookup = new Map<string, number>()
let lastTreeData: DataNode[] | null = null

/**
 * Builds a lookup map for individual content item depths within the tree hierarchy
 * @param treeData - The tree data structure containing content nodes
 * @returns Map where keys are content item IDs and values are their depth level (0-based)
 */
const buildLookup = (treeData: DataNode[]): Map<string, number> => {
    const lookup = new Map<string, number>()

    const traverse = (nodes: DataNode[], depth: number = 0) => {
        for (const node of nodes) {
            // store depth for each content item in the current node
            node.contentItemNodes?.forEach((item) => {
                if (item.id) {
                    lookup.set(item.id, depth)
                }
            })

            // recursively traverse children at increased depth
            if (node.children?.length) {
                traverse(node.children, depth + 1)
            }
        }
    }

    traverse(treeData)
    return lookup
}

/**
 * Builds a lookup map for content item hierarchy depths (including all descendant depths)
 * @param treeData - The tree data structure containing content nodes
 * @returns Map where keys are content item IDs and values are their maximum hierarchy depth
 */
const buildHierarchyDepthLookup = (
    treeData: DataNode[]
): Map<string, number> => {
    const lookup = new Map<string, number>()

    const traverse = (nodes: DataNode[], depth: number = 0) => {
        for (const node of nodes) {
            node.contentItemNodes?.forEach((item) => {
                if (item.id) {
                    // calculate the deepest point in this node's subtree
                    const maxChildDepth = getMaxChildDepth(node, depth)
                    lookup.set(item.id, maxChildDepth)
                }
            })

            if (node.children?.length) {
                traverse(node.children, depth + 1)
            }
        }
    }

    traverse(treeData)
    return lookup
}

/**
 * Calculates the maximum depth within a node's subtree (including all descendants)
 * @param node - The data node to analyze
 * @param currentDepth - The depth level of the current node
 * @returns The maximum depth found in the node's entire subtree
 */
const getMaxChildDepth = (node: DataNode, currentDepth: number): number => {
    // leaf nodes return their own depth
    if (!node.children?.length) {
        return currentDepth
    }

    let maxDepth = currentDepth
    const traverse = (children: DataNode[], depth: number) => {
        for (const child of children) {
            maxDepth = Math.max(maxDepth, depth)
            if (child.children?.length) {
                traverse(child.children, depth + 1)
            }
        }
    }

    traverse(node.children, currentDepth + 1)
    return maxDepth
}

/**
 * Finds the individual depth of a content item within the tree structure
 * @param contentItemId - The ID of the content item to locate
 * @param treeData - The tree data structure to search within
 * @returns The depth level (0-based) of the content item, or -1 if not found
 */
export const findContentItemDepth = (
    contentItemId: string,
    treeData: DataNode[]
): number => {
    if (!contentItemId || !treeData?.length) {
        return -1
    }

    // rebuild lookup maps when tree data reference changes (cache invalidation)
    if (lastTreeData !== treeData) {
        depthLookup = buildLookup(treeData)
        hierarchyDepthLookup = buildHierarchyDepthLookup(treeData)
        lastTreeData = treeData
    }

    return depthLookup.get(contentItemId) ?? -1
}

/**
 * Finds the maximum depth of a content item's hierarchy including all descendants
 * @param contentItemId - The ID of the content item to analyze
 * @param treeData - The tree data structure to search within
 * @returns The maximum depth in the content item's hierarchy, or -1 if not found
 */
export const findContentItemHierarchyDepth = (
    contentItemId: string,
    treeData: DataNode[]
): number => {
    if (!contentItemId || !treeData?.length) {
        return -1
    }

    // rebuild lookup maps when tree data reference changes (cache invalidation)
    if (lastTreeData !== treeData) {
        depthLookup = buildLookup(treeData)
        hierarchyDepthLookup = buildHierarchyDepthLookup(treeData)
        lastTreeData = treeData
    }

    return hierarchyDepthLookup.get(contentItemId) ?? -1
}

/**
 * Validates if a promote/demote operation would exceed the maximum depth limit
 * @param contentItemId - The ID of the content item to validate
 * @param action - The promote/demote action to validate
 * @param treeData - The current tree data structure
 * @param maxDepth - Maximum allowed depth (default: 5)
 * @returns True if the operation would exceed the depth limit, false otherwise
 */
export const wouldExceedDepthLimit = (
    contentItemId: string,
    action: PromoteDemoteAction,
    treeData: DataNode[],
    maxDepth: number = 5
): boolean => {
    const currentDepth = findContentItemDepth(contentItemId, treeData)
    const hierarchyDepth = findContentItemHierarchyDepth(
        contentItemId,
        treeData
    )

    // return false for invalid content items (fail-safe)
    if (currentDepth === -1 || hierarchyDepth === -1) {
        return false
    }

    // for demote: check if moving content deeper would exceed max depth
    if (action === PromoteDemoteActions.Demote) {
        const newDepth = currentDepth + 1
        const depthIncrease = newDepth - currentDepth // always 1 for demote
        return hierarchyDepth + depthIncrease > maxDepth
    }

    // for promote: reduces depth, so never exceeds limits
    return false
}
